import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Download, ExternalLink, Search, UserPlus, X } from 'lucide-react';
import type { ClientRow } from '@contracts';
import { ODOO_SB_WEB_URL, ODOO_WEB_URL } from '@/lib/config';
import { canOpenOdooLinks } from '@/services/authService';
import { exportExcel } from '@/lib/exportData';

const money = (n: number) =>
  n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });
const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('es-MX', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};
// SmartBamboo vive en su propio Odoo → los clientes/órdenes SB linkean a ODOO_SB_WEB_URL.
const partnerUrl = (id: number, base: string) => `${base}/web#id=${id}&model=res.partner&view_type=form`;
const orderUrl = (id: number, base: string) => `${base}/web#id=${id}&model=pos.order&view_type=form`;

type SortCol = 'name' | 'date' | 'pos' | 'seller' | 'orders' | 'total';
type SortDir = 'asc' | 'desc';
const PAGE_OPTS = [10, 50, 100, 0];

export interface ClientsDrilldownModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  clients: ClientRow[];
  loading?: boolean;
}

/** Drill-down de Clientes Registrados: filas expandibles con sus órdenes; nombre y
 *  órdenes con link a Odoo. */
export function ClientsDrilldownModal({ open, onClose, title, clients, loading }: ClientsDrilldownModalProps) {
  const [q, setQ] = useState('');
  const [pos, setPos] = useState('');
  const [sortCol, setSortCol] = useState<SortCol>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => setPage(0), [q, pos, sortCol, sortDir, pageSize, clients]);

  const posNames = useMemo(
    () => Array.from(new Set(clients.map((c) => c.posName).filter(Boolean))).sort(),
    [clients],
  );
  const dates = useMemo(() => clients.map((c) => c.date).filter(Boolean).sort(), [clients]);

  const toggleSort = (col: SortCol) => {
    if (col === sortCol) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortCol(col);
      setSortDir(col === 'name' ? 'asc' : 'desc');
    }
  };
  const toggleRow = (id: number) =>
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let rows = pos ? clients.filter((c) => c.posName === pos) : clients;
    if (term) {
      rows = rows.filter((c) => [c.name, c.seller, c.posName].some((f) => f?.toLowerCase().includes(term)));
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    const str = (a: string, b: string) => a.localeCompare(b) * dir;
    const sorted = [...rows];
    sorted.sort((a, b) => {
      switch (sortCol) {
        case 'date': return str(a.date, b.date);
        case 'orders': return (a.ordersCount - b.ordersCount) * dir;
        case 'total': return ((a.total ?? 0) - (b.total ?? 0)) * dir;
        case 'pos': return str(a.posName, b.posName);
        case 'seller': return str(a.seller, b.seller);
        default: return str(a.name, b.name);
      }
    });
    return sorted;
  }, [clients, q, pos, sortCol, sortDir]);

  const pages = pageSize ? Math.max(1, Math.ceil(filtered.length / pageSize)) : 1;
  const start = pageSize ? page * pageSize : 0;
  const rows = pageSize ? filtered.slice(start, start + pageSize) : filtered;

  const doExport = () =>
    void exportExcel(
      'clientes_registrados',
      filtered.map((c) => ({
        Cliente: c.name,
        Registro: c.date,
        'Sucursal de registro': c.posName,
        'Agregado por': c.seller,
        Región: c.region,
        Órdenes: c.ordersCount,
        'Primera orden': c.firstOrder?.ref ?? '',
      })),
    );

  if (!open) return null;

  const arrow = (col: SortCol) => (sortCol === col ? (sortDir === 'asc' ? '↑' : '↓') : '');
  const withOrders = filtered.filter((c) => c.ordersCount > 0).length;
  // SmartBamboo (segmentos Altas/Oro/Plata) trae `total` (compras) y no tiene Sucursal/Agregado por.
  const isSb = clients.some((c) => c.total != null);
  const odooBase = isSb ? ODOO_SB_WEB_URL : ODOO_WEB_URL;
  const cols: { col: SortCol; label: string; right: boolean }[] = isSb
    ? [
        { col: 'name', label: 'Cliente', right: false },
        { col: 'date', label: 'Registro', right: false },
        { col: 'pos', label: 'Sucursal', right: false },
        { col: 'total', label: 'Compras', right: true },
        { col: 'orders', label: 'Órdenes', right: true },
      ]
    : [
        { col: 'name', label: 'Cliente', right: false },
        { col: 'date', label: 'Registro', right: false },
        { col: 'pos', label: 'Sucursal de registro', right: false },
        { col: 'seller', label: 'Agregado por', right: false },
        { col: 'orders', label: 'Órdenes', right: true },
      ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[90dvh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center gap-4 bg-gradient-to-r from-violet-700 via-purple-600 to-fuchsia-600 px-5 py-4 text-white">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-white/25 bg-white/15">
            <UserPlus className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-xl font-black leading-tight">{title}</h2>
            <p className="mt-0.5 truncate text-sm text-white/80">
              <span className="font-semibold text-white">{clients.length} clientes</span> registrados
              {' · '}{withOrders} con órdenes
              {dates.length > 0 && <> {' · '}{fmtDate(dates[0]).slice(0, 5)} — {fmtDate(dates[dates.length - 1]).slice(0, 5)}</>}
            </p>
          </div>
          <button onClick={onClose} title="Cerrar" className="shrink-0 rounded-lg p-1.5 text-white/80 hover:bg-white/15">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-5 py-3">
          {posNames.length > 1 && (
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Sucursal</span>
              <select
                value={pos}
                onChange={(e) => setPos(e.target.value)}
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-400"
              >
                <option value="">Todas</option>
                {posNames.map((p) => (<option key={p} value={p}>{p}</option>))}
              </select>
            </div>
          )}
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar cliente, agregado por, sucursal…"
              className="w-full rounded-xl border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-violet-400"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Mostrar</span>
            <div className="flex gap-1">
              {PAGE_OPTS.map((p) => (
                <button
                  key={p}
                  onClick={() => setPageSize(p)}
                  className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${
                    pageSize === p ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {p === 0 ? 'Todos' : p}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={doExport}
            title="Descargar Excel"
            className="flex items-center gap-1.5 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 hover:border-emerald-400 hover:text-emerald-600"
          >
            <Download className="h-4 w-4" /> Excel
          </button>
        </div>

        {/* Tabla */}
        <div className="min-h-0 flex-1 overflow-auto">
          {loading ? (
            <div className="p-10 text-center text-sm text-slate-400">Cargando clientes…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-400">Sin clientes para los filtros actuales.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500 shadow-sm">
                <tr>
                  <th className="w-8" />
                  {cols.map((h) => (
                    <th
                      key={h.col}
                      onClick={() => toggleSort(h.col)}
                      className={`cursor-pointer select-none px-4 py-2.5 font-semibold hover:text-violet-600 ${h.right ? 'text-right' : ''}`}
                    >
                      {h.label}
                      <span className="ml-0.5 inline-block w-2 text-violet-600">{arrow(h.col)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const isOpen = expanded.has(c.id);
                  const hasOrders = c.ordersCount > 0;
                  const canExpand = c.orders.length > 0; // SB no trae detalle de órdenes → no expande
                  return [
                    <tr
                      key={c.id}
                      onClick={canExpand ? () => toggleRow(c.id) : undefined}
                      className={`border-b border-slate-100 ${canExpand ? 'cursor-pointer hover:bg-violet-50/50' : 'hover:bg-slate-50'} ${isOpen ? 'bg-violet-50/60' : ''}`}
                    >
                      <td className="pl-3 text-slate-400">
                        {canExpand && <ChevronRight className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} />}
                      </td>
                      <td className="px-4 py-2.5">
                        {canOpenOdooLinks() ? (
                          <a
                            href={partnerUrl(c.id, odooBase)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title={isSb ? 'Abrir cliente en Odoo SmartBamboo' : 'Abrir cliente en Odoo'}
                            className="group inline-flex items-center gap-1 font-semibold text-violet-700 hover:underline"
                          >
                            {c.name}
                            <ExternalLink className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                          </a>
                        ) : (
                          <span className="font-semibold text-slate-700">{c.name}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-slate-500">{fmtDate(c.date)}</td>
                      {isSb ? (
                        <>
                          <td className="px-4 py-2.5">{c.posName || '—'}</td>
                          <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-slate-700">
                            {c.total != null ? c.total.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }) : '—'}
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-2.5">{c.posName || '—'}</td>
                          <td className="px-4 py-2.5">{c.seller || '—'}</td>
                        </>
                      )}
                      <td className="px-4 py-2.5 text-right tabular-nums">
                        {hasOrders ? (
                          <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-700">{c.ordersCount}</span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    </tr>,
                    isOpen && canExpand ? (
                      <tr key={`${c.id}-exp`} className="bg-slate-50">
                        <td />
                        <td colSpan={cols.length} className="px-4 py-2">
                          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                            {c.orders.map((o, idx) => (
                              <div
                                key={o.id}
                                className="flex items-center gap-3 border-b border-slate-50 px-3 py-1.5 text-xs last:border-0"
                              >
                                {canOpenOdooLinks() ? (
                                  <a
                                    href={orderUrl(o.id, odooBase)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group inline-flex items-center gap-1 font-mono font-semibold text-violet-700 hover:underline"
                                  >
                                    {o.ref}
                                    <ExternalLink className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                                  </a>
                                ) : (
                                  <span className="font-mono font-semibold text-slate-700">{o.ref}</span>
                                )}
                                {idx === 0 && (
                                  <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">1ª orden</span>
                                )}
                                <span className="text-slate-400">{fmtDate(o.date)}</span>
                                <span className="text-slate-500">{o.posName}</span>
                                <span className="ml-auto font-bold tabular-nums text-emerald-700">{money(o.total)}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ) : null,
                  ];
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 border-t border-slate-100 bg-slate-50/60 px-5 py-3 text-sm">
          <span className="text-slate-600">
            Clientes registrados: <span className="font-bold text-violet-700">{filtered.length}</span>
          </span>
          <div className="ml-auto flex items-center gap-3 text-slate-500">
            <span className="text-xs">
              {pageSize ? `${start + 1}–${Math.min(start + pageSize, filtered.length)} de ${filtered.length}` : `${filtered.length} clientes`}
            </span>
            {pageSize > 0 && pages > 1 && (
              <div className="flex items-center gap-2">
                <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="rounded-lg px-2 py-1 hover:bg-slate-200 disabled:opacity-40">‹</button>
                <span className="text-xs">{page + 1} / {pages}</span>
                <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1} className="rounded-lg px-2 py-1 hover:bg-slate-200 disabled:opacity-40">›</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
